import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ToastTone } from "@/ui/controls";
import { Toast, ToastViewport } from "@/ui/controls";

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  exiting?: boolean;
}

// Match the Calendar integration notifications so dashboard feedback has one rhythm.
const AUTO_DISMISS_MS = 4200;
const EXIT_ANIM_MS = 220;

/**
 * Self-contained toast queue for dashboard sections. Returns a `push` function and a
 * ready-to-render `<viewport />`. Kept local (rather than app-global) so the polish ships
 * without wider plumbing; the underlying Toast/ToastViewport primitives are reused.
 */
export function useDashboardToasts() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIM_MS);
  }, []);

  const push = useCallback(
    (tone: ToastTone, title: string, description?: string) => {
      const id = nextId.current++;
      setItems((prev) => [...prev, { id, tone, title, description }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const success = useCallback((title: string, description?: string) => push("success", title, description), [push]);
  const error = useCallback((title: string, description?: string) => push("danger", title, description), [push]);
  const info = useCallback((title: string, description?: string) => push("info", title, description), [push]);

  const viewportContent = (
    <ToastViewport position="bottom-right">
      {items.map((t) => (
        <Toast
          key={t.id}
          tone={t.tone}
          title={t.title}
          description={t.description}
          exiting={t.exiting}
          onDismiss={() => dismiss(t.id)}
          role={t.tone === "danger" ? "alert" : "status"}
        />
      ))}
    </ToastViewport>
  );
  // Some dashboard sections use CSS zoom to fit large forms in the viewport. A fixed toast
  // nested under that container is scaled and anchored against it, so portal the notification
  // beside the Calendar viewport at the document level.
  const viewport = typeof document === "undefined"
    ? viewportContent
    : createPortal(viewportContent, document.body);

  return { push, success, error, info, viewport };
}

// Retain the original name for existing billing consumers.
export const useBillingToasts = useDashboardToasts;
